// hqPrefs.js — per-user HQ home preferences + Right Now preset helpers.
//
// The lightweight visual prefs (view/sort/hiddenSections/collapsedSections)
// live in localStorage keyed by user id. The Right Now preset LIBRARY lives
// on the backend (`GET/PUT /api/me/rightnow_presets`) so it syncs across
// devices — we only cache the caller's `currentPresetId` locally to avoid a
// flash on reload.

const DEFAULTS = {
  view: "compact",              // "compact" | "detail"
  sort: "custom",               // "custom" | "attention" | "alphabetical"
  hiddenSections: [],           // string[] of section keys hidden by user
  collapsedSections: [],        // string[] of section keys currently collapsed
  currentPresetId: null,        // last-selected Right Now preset id (server-authoritative)
};

const KEY = (userId) => `alias_hq_home_prefs::${userId || "anon"}`;

export function loadPrefs(userId) {
  try {
    const raw = localStorage.getItem(KEY(userId));
    if (!raw) return { ...DEFAULTS };
    const parsed = JSON.parse(raw);
    return { ...DEFAULTS, ...parsed };
  } catch {
    return { ...DEFAULTS };
  }
}

export function savePrefs(userId, prefs) {
  try {
    localStorage.setItem(KEY(userId), JSON.stringify(prefs));
  } catch { /* quota or private mode — silently drop */ }
}

export const HQ_PREFS_DEFAULTS = DEFAULTS;

// Ordered list of Right Now sections the admin can hide.
export const RIGHT_NOW_SECTIONS = [
  { key: "overdue",   label: "Overdue tasks" },
  { key: "health",    label: "Project health" },
  { key: "decisions", label: "Pending decisions" },
  { key: "upcoming",  label: "Upcoming" },
  { key: "warnings",  label: "Budget warnings" },
];

// The locked safe-reset preset id — always present, cannot be renamed
// or deleted. Kept in sync with the backend constant.
export const ALL_PRESET_ID = "preset_all";

// Compare two arrays of section keys for equality regardless of order.
function _sameSet(a, b) {
  const s1 = new Set(a || []);
  const s2 = new Set(b || []);
  if (s1.size !== s2.size) return false;
  for (const v of s1) if (!s2.has(v)) return false;
  return true;
}

/**
 * Returns true when the caller's current hidden/collapsed layout differs
 * from the given preset. Used to render the "Modified" chip + the
 * Update / Save-as-new action buttons.
 */
export function isPresetModified(preset, hiddenSections, collapsedSections) {
  if (!preset) return false;
  return !(
    _sameSet(preset.hiddenSections, hiddenSections)
    && _sameSet(preset.collapsedSections, collapsedSections)
  );
}
